import React, { memo, useEffect, useState, useRef } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye, FiEdit3, FiTrash2 } from "react-icons/fi";
import { Link } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import usePermission from "../../hooks/usePermission";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

const TableCell = memo(({ userId, fetchUsers }) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/customers/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
            },
          },
        );

        setIsChecked(response.data.status === "active");
      } catch (error) {
        console.error("❌ Kullanıcı durumu alınamadı:", error);
      }
    };

    fetchUserStatus();
  }, [userId]);

  const toggleStatus = async () => {
    const newStatus = isChecked ? "inactive" : "active";
    setIsChecked(!isChecked);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/customers/update-status/${userId}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        },
      );
    } catch (error) {
      console.error("❌ Durum güncellenemedi:", error);
      setIsChecked(isChecked);
    }
  };

  return (
    <div className="form-check form-switch text-ebd d-flex align-items-end justify-content-end">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleStatus}
        style={{
          cursor: "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
      <span
        className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
        style={{
          padding: "6px 12px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          transition: "0.3s ease-in-out",
        }}
      ></span>
    </div>
  );
});

const VerificationSwitch = memo(({ userId, field, value, fetchUsers }) => {
  const [isChecked, setIsChecked] = useState(value === 1);

  useEffect(() => {
    setIsChecked(value === 1);
  }, [value]);

  const toggleVerification = async () => {
    const newStatus = isChecked ? 0 : 1;

    try {
      const token = localStorage.getItem("admin_token");

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/customers/update-verification/${userId}`,
        { [field]: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setIsChecked(newStatus);
    } catch (error) {
      console.error("❌ Durum güncellenemedi:", error);
      setIsChecked(isChecked);
    }
  };

  return (
    <div className="form-check form-switch text-start d-flex align-items-start justify-content-start">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleVerification}
        style={{
          cursor: "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
      <span
        className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
        style={{
          padding: "6px 12px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          transition: "0.3s ease-in-out",
        }}
      />
    </div>
  );
});

export const formatPhone = (phone) => {
  if (!phone) return "-";

  // Sadece rakamları al
  let digits = phone.toString().replace(/\D/g, "");

  // +90 ile gelenler (90555...)
  if (digits.startsWith("90") && digits.length === 12) {
    return `+90 (${digits.slice(2, 5)}) ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`;
  }

  // 0 ile başlayanlar (0555...)
  if (digits.startsWith("0") && digits.length === 11) {
    return `0 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
  }

  // Direkt 5 ile başlayanlar (555...)
  if (digits.startsWith("5") && digits.length === 10) {
    return `0 (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  }

  // Diğer ülke kodları (+49123...)
  if (digits.length > 11) {
    const countryCode = digits.slice(0, digits.length - 10);
    const number = digits.slice(-10);
    return `+${countryCode} (${number.slice(0, 3)}) ${number.slice(3, 6)} ${number.slice(6, 8)} ${number.slice(8, 10)}`;
  }

  return phone; // fallback
};

const CustomersTable = () => {
  const checkboxRef = useRef(null);
  const [users, setUsers] = useState([]);
  const { canView, canEdit, canDelete } = usePermission("static_users");

  // Çeviri için gerekli hook'lar
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const handleSoftDelete = (id) => {
    confirmAlert({
      title: getText(
        "SOFT_DELETE_CONFIRM_TITLE",
        "Kullanıcıyı Çöp Kutusuna Taşı",
      ),
      message: getText(
        "SOFT_DELETE_CONFIRM_MSG",
        "Bu kullanıcıyı çöpe taşımak istediğinizden emin misiniz?",
      ),
      buttons: [
        {
          label: getText("YES_SOFT_DELETE"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/user/soft-delete/${id}`,
                {},
                {
                  headers: { Authorization: `Bearer ${token}` },
                },
              );
              toast.success(
                getText("SUCCESS_USER_SOFT_DELETED"),
              );
              setUsers((prev) => prev.filter((item) => item.id !== id)); // Kullanıcıyı listeden çıkar
            } catch (error) {
              console.error("Soft-delete hatası:", error);
              toast.error(
                getText(
                  "ERROR_USER_SOFT_DELETE_FAILED",
                  "Çöpe taşıma başarısız!",
                ),
              );
            }
          },
        },
        {
          label: getText("CANCEL"),
          onClick: () =>
            toast.info(getText("SOFT_DELETE_CANCELLED")),
        },
      ],
    });
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("admin_token");

      if (!token) {
        console.error("🚨 Token bulunamadı, kullanıcı giriş yapmamış.");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/customers/list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      console.log("📌 API'den Gelen Kullanıcı Verisi:", response.data);

      setUsers(response.data);
    } catch (error) {
      console.error("❌ Kullanıcı verileri alınamadı:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const columns = [
    {
      accessorKey: "id",
      header: () => getText("TABLE_ID_LABEL"),
    },
    {
      accessorKey: "profileImage",
      header: () => getText("TABLE_PROFILE_IMAGE_LABEL"),
      enableSorting: false,
      cell: (info) => {
        console.log(
          "🖼 React İçinde Profil Resmi Bilgisi:",
          info.row.original.customer.img,
        );

        const imagePath = info.row.original.customer?.img;
        const imageUrl = imagePath
          ? `${process.env.REACT_APP_API_BASE_URL}${imagePath}` // 🔹 Veritabanında sadece `/uploads/...` var, burada tam URL ekleniyor
          : `${process.env.REACT_APP_API_BASE_URL}/uploads/ProfileImages/default.svg`;

        return (
          <div style={{ textAlign: "start" }}>
            <img
              src={imageUrl}
              alt="Profil"
              onError={(e) =>
                (e.target.src = `${process.env.REACT_APP_API_BASE_URL}/uploads/ProfileImages/default.svg`)
              }
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #ddd",
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "customer",
      header: () => getText("TABLE_USERS_LABEL"),
      cell: (info) => {
        const user = info.getValue();
        return (
          <div className="hstack gap-3">
            <div>
              <span className="text-truncate-1-line">{user?.name}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: () => getText("TABLE_EMAIL_LABEL"),
      cell: (info) => (
        <a href={`mailto:${info.getValue()}`}>{info.getValue()}</a>
      ),
    },
    {
      accessorKey: "phone",
      header: () => getText("TABLE_PHONE_LABEL"),
      cell: (info) => {
        const phone = info.getValue();
        return <a href={`tel:${phone}`}>{formatPhone(phone)}</a>;
      },
    },

    {
      accessorKey: "date",
      header: () => (
        <div style={{ textAlign: "end" }}>
          {getText("TABLE_DATE_LABEL")}
        </div>
      ),
      meta: { headerClassName: "text-end", className: "text-end date-column" },
      cell: (info) => {
        const rawDate = info.getValue();

        if (!rawDate) {
          return <span>Bilinmiyor</span>;
        }

        try {
          const formattedDate = new Intl.DateTimeFormat("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }).format(new Date(rawDate));

                  return (
          <div style={{ textAlign: 'end' }}>
            {formattedDate}
          </div>
        );
        } catch (error) {
          console.error("⚠️ Geçersiz tarih:", rawDate);
          return <span>Bilinmiyor</span>;
        }
      },
    },

    {
      accessorKey: "status",
      header: () => (
        <div style={{ textAlign: "end"}}>
          {getText("TABLE_STATUS_LABEL")}
        </div>
      ),
      meta: { headerClassName: "text-end", className: "text-end" },
      cell: (info) => (
        <TableCell userId={info.row.original.id} fetchUsers={fetchUsers} />
      ),
    },

    {
      accessorKey: "actions",
      header: () => (
        <div style={{ textAlign: "right", paddingRight: "50px" }}>
          {getText("TABLE_ACTIONS_LABEL")}
        </div>
      ),
      enableSorting: false,
      cell: ({ row }) => {
        const userId = row.original.id;

        return (
          <div
            className="hstack gap-2 justify-content-end"
            style={{ paddingRight: "20px" }}
          >
            {canView && (
              <Link
                to={`/customers/view/${userId}`}
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={getText("VIEW")}
              >
                <FiEye />
              </Link>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault(); // sayfa yenilemeyi engelle
                  handleSoftDelete(userId);
                }}
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={getText("DELETE")}
              >
                <FiTrash2 />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return <Table data={users} columns={columns} />;
};

export default CustomersTable;
